import { formatDay, companyIsoDay } from '../ui/dates';
import {
  createListItem,
  deleteListItem,
  getListItem,
  getListItems,
  odataString,
  updateListItem,
} from '../graph/lists';
import { executeListOps, type ListOp } from '../graph/listOps';
import { dataViaApi } from '../api/mode';
import { GraphError } from '../graph/client';
import { deleteAttachmentsFolder } from '../graph/library';
import { LISTS } from './listNames';
import { addTaskComment, addTaskEvent, type TaskEventKind } from './taskComments';
import { newClientKey } from './clientKey';
import { equalsIgnoreCase } from './scope';
import { parseReopenNote, reopenBody, type ReopenNote } from './reopenReason';
import { EXTENSION_REASON_MAX, extensionDateProblem, parseExtensionRequest, type ExtensionRequest } from './taskRules';

/**
 * Task lifecycle (spec §6): open → complete (with CompletedByName for
 * personal accountability behind shared store accounts) → reopened.
 * Complete = done; no verification gate. Notifications are Phase 4 flows
 * reacting to these rows — the app only writes state.
 */

export type TaskStatus = 'open' | 'complete' | 'reopened';

export interface ServerTask {
  id: number;
  title: string;
  description: string;
  storeId: number;
  /** null = ad-hoc task not tied to an audit. */
  auditId: number | null;
  questionId: string;
  /** Set when the task targets one sample-group entry. */
  sampleIndex: number | null;
  assignedTo: string;
  createdBy: string;
  /** Creator's display name when captured at creation ('' on older rows). */
  createdByName: string;
  /** ISO date. */
  dueDate: string;
  status: TaskStatus;
  completedAt: string | null;
  completedByName: string;
  completionNotes: string;
  /**
   * When anything last happened on the task — a comment, a timeline event
   * (complete, reopen, reassign, due date, file). Server-stamped
   * (api/src/writeRun.ts); null on rows from before the column existed:
   * fall back to `createdAt` (taskActivity.ts lastActivityOf).
   */
  lastActivityAt: string | null;
  /**
   * Files on the task (Tasks.FileCount, server-stamped: 0 at create, the
   * count after each upload) — the 📎 on task rows, so a completion without
   * proof stands out (round 2, 2026-10-22). Undefined = unknown (a task made
   * before 2026-10-25, or written straight to SharePoint): never badged
   * "No photo or PDF" (QA + security 2026-10-25).
   */
  fileCount?: number | undefined;
  /** The latest reopen, while the task is reopened (server-stamped; null on older rows). */
  reopenNote?: ReopenNote | null;
  /** The row carries a reopen note (server-owned since 2026-10-25): reopened at least once since then, whatever its status now. */
  everReopened?: boolean;
  /** "Proof required" (B1): a photo or PDF must be uploaded since `proofSince` to complete. */
  proofRequired?: boolean;
  proofSince?: string | null;
  /** Who completed it (account; server-stamped since 2026-10-25, '' on older rows). */
  completedByUpn?: string;
  /** Flow 3's last overdue reminder (its cadence stamp; null = never) — B5's next-step line. */
  lastOverdueReminderAt?: string | null;
  /** B2: the store's pending "ask for more time" request (open/reopened tasks only). */
  extensionRequest?: ExtensionRequest | null;
  /** SharePoint's own creation time of the row (ISO); null when unknown. */
  createdAt: string | null;
}

interface TaskFields {
  Title: string;
  TaskDescription: string;
  StoreId: number;
  AuditId: number | null;
  QuestionId: string;
  SampleIndex: number | null;
  AssignedTo: string;
  CreatedByUpn: string;
  CreatedByName: string;
  DueDate: string;
  TaskStatus: string;
  CompletedAt: string | null;
  CompletedByName: string;
  CompletionNotes: string;
  /** Server-owned (never sent by the app — the server strips it). */
  LastActivityAt: string | null;
  /** Server-owned: files in the task's folder, recounted after each upload (round 2, 2026-10-22). */
  FileCount: number | null;
  /** Server-owned: the latest reopen as JSON {by, at, reason} (2026-10-25 — task cards show it). */
  ReopenNote: string | null;
  /** B1 (2026-10-25): completing needs a photo/PDF uploaded since ProofSince. */
  ProofRequired: boolean | null;
  /** Server-owned: stamped at create and every reopen. */
  ProofSince: string | null;
  /** Server-owned: the account that completed it (B3 — a store undoes only its own). */
  CompletedByUpn: string | null;
  /** B2: the store's pending "more time" request (JSON; server-checked). */
  ExtensionRequest: string | null;
  /** Flow 3's overdue-reminder cadence stamp (the flow's; the app never writes it). */
  LastOverdueReminderAt: string | null;
  /** Names the create (src/data/clientKey.ts); set once, never edited. */
  ClientKey: string;
}

function toTask(id: string, f: Partial<TaskFields>, createdAt: string | null = null): ServerTask {
  const status: TaskStatus =
    f.TaskStatus === 'complete' || f.TaskStatus === 'reopened'
      ? f.TaskStatus
      : 'open';
  return {
    id: Number(id),
    title: f.Title ?? '',
    description: f.TaskDescription ?? '',
    storeId: f.StoreId ?? 0,
    auditId: f.AuditId ?? null,
    questionId: f.QuestionId ?? '',
    sampleIndex: f.SampleIndex ?? null,
    assignedTo: f.AssignedTo ?? '',
    createdBy: f.CreatedByUpn ?? '',
    createdByName: f.CreatedByName ?? '',
    dueDate: (f.DueDate ?? '').slice(0, 10),
    status,
    completedAt: f.CompletedAt ?? null,
    completedByName: f.CompletedByName ?? '',
    completionNotes: f.CompletionNotes ?? '',
    lastActivityAt: f.LastActivityAt ?? null,
    fileCount: typeof f.FileCount === 'number' && Number.isFinite(f.FileCount) ? Math.max(0, Math.floor(f.FileCount)) : undefined,
    reopenNote: status === 'reopened' ? parseReopenNote(f.ReopenNote) : null,
    everReopened: typeof f.ReopenNote === 'string' && f.ReopenNote.trim() !== '',
    proofRequired: f.ProofRequired === true,
    proofSince: f.ProofSince ?? null,
    completedByUpn: f.CompletedByUpn ?? '',
    extensionRequest: status === 'complete' ? null : parseExtensionRequest(f.ExtensionRequest),
    lastOverdueReminderAt: f.LastOverdueReminderAt ?? null,
    createdAt,
  };
}

/**
 * Open work (open + reopened), oldest due first. TaskStatus and DueDate are
 * both indexed. Role scoping is applied by the caller against its visible
 * stores (the list is portfolio-wide).
 */
export async function getOpenTasks(): Promise<ServerTask[]> {
  // Two indexed `eq` queries, merged — an `or` across one column may not use
  // the index once Tasks passes 5,000 rows (golive-review G9).
  const [open, reopened] = await Promise.all(
    (['open', 'reopened'] as const).map((status) =>
      getListItems<TaskFields>(LISTS.tasks, {
        filter: `fields/TaskStatus eq ${odataString(status)}`,
        orderBy: 'fields/DueDate',
      }),
    ),
  );
  return [...open!, ...reopened!]
    .map((i) => toTask(i.id, i.fields, i.createdDateTime ?? null))
    .sort((a, b) => (a.dueDate || '9999').localeCompare(b.dueDate || '9999') || a.id - b.id);
}

/**
 * The most recently COMPLETED tasks (golive-review G6: CompletedAt is
 * indexed by provisioning since 2026-10-18). Until that re-run reaches the
 * tenant, SharePoint refuses $orderby on the column — then this falls back
 * to the old DueDate order, so the app can merge before provisioning runs.
 */
export async function getCompletedTasks(limit = 200): Promise<ServerTask[]> {
  const query = (orderBy: string) =>
    getListItems<TaskFields>(LISTS.tasks, {
      filter: `fields/TaskStatus eq ${odataString('complete')}`,
      orderBy,
      top: limit,
    });
  let items;
  try {
    items = await query('fields/CompletedAt desc');
  } catch (err) {
    if (!(err instanceof GraphError && err.status === 400)) throw err;
    items = await query('fields/DueDate desc');
  }
  return items.map((i) => toTask(i.id, i.fields, i.createdDateTime ?? null)).slice(0, limit);
}

/**
 * Tasks completed on or after `since` (ISO instant) — TaskStatus and
 * CompletedAt are both indexed (golive-review G6). The period reports read
 * this plus the open tasks: together they hold every task created in or
 * after the period (a task created then is either still open or completed
 * after the period start). No row cap — a period's completions are far
 * below the 5,000 limit.
 */
export async function getCompletedTasksSince(since: string): Promise<ServerTask[]> {
  const items = await getListItems<TaskFields>(LISTS.tasks, {
    // Date clause FIRST: SharePoint narrows by the first indexed clause, and "complete" alone grows past 5,000 (QA 2026-10-25; audits.ts does the same).
    filter: `fields/CompletedAt ge ${odataString(since)} and fields/TaskStatus eq ${odataString('complete')}`,
  });
  return items.map((i) => toTask(i.id, i.fields, i.createdDateTime ?? null));
}

/** Every task for one store (StoreId indexed), any status. */
export async function getTasksForStore(storeId: number): Promise<ServerTask[]> {
  const items = await getListItems<TaskFields>(LISTS.tasks, {
    filter: `fields/StoreId eq ${storeId}`,
  });
  return items.map((i) => toTask(i.id, i.fields, i.createdDateTime ?? null));
}

/** One task by list item id — the notification emails deep-link here. */
export async function getTaskById(taskId: number): Promise<ServerTask | null> {
  const item = await getListItem<TaskFields>(LISTS.tasks, taskId);
  return item ? toTask(item.id, item.fields, item.createdDateTime ?? null) : null;
}

/** All tasks created from one audit (AuditId indexed), any status. */
export async function getTasksForAudit(auditId: number): Promise<ServerTask[]> {
  const items = await getListItems<TaskFields>(LISTS.tasks, {
    filter: `fields/AuditId eq ${auditId}`,
  });
  return items.map((i) => toTask(i.id, i.fields, i.createdDateTime ?? null));
}

export interface NewTask {
  title: string;
  description: string;
  storeId: number;
  auditId?: number;
  questionId?: string;
  assignedTo: string;
  dueDate: string;
  createdBy: string;
  createdByName?: string;
  /** The same while the person retries the same new task (SubmissionKey); default = a fresh one. */
  clientKey?: string;
  /** "Proof required (photo or PDF to complete)" (B1, 2026-10-25). */
  proofRequired?: boolean;
}

/** Ad-hoc task creation (in-audit creation rides the sync engine instead). */
export async function createTask(task: NewTask): Promise<ServerTask> {
  const created = await createListItem<TaskFields>(LISTS.tasks, {
    Title: task.title,
    TaskDescription: task.description,
    StoreId: task.storeId,
    AuditId: task.auditId ?? null,
    QuestionId: task.questionId ?? '',
    AssignedTo: task.assignedTo,
    CreatedByUpn: task.createdBy,
    CreatedByName: task.createdByName ?? '',
    DueDate: task.dueDate,
    TaskStatus: 'open',
    ClientKey: task.clientKey ?? newClientKey('task'),
    // Only when ticked (B1); unticked = blank = No.
    ...(task.proofRequired ? { ProofRequired: true } : {}),
  });
  return toTask(created.id, created.fields, created.createdDateTime ?? null);
}

/** Who performed a task action — for the ticket timeline (taskComments.ts). */
export interface TaskActor {
  upn: string;
  /** Display/typed name; shared store accounts get the person's typed name. */
  name: string;
}

/**
 * Timeline writes are best-effort: the state change they describe must not
 * be rolled back (or surface as an error) because the event row failed.
 */
function logEvent(
  taskId: number,
  kind: TaskEventKind,
  body: string,
  actor: TaskActor,
  targetUpn = '',
  clientKey?: string,
): Promise<void> {
  return addTaskEvent(taskId, kind, body, actor.upn, actor.name, targetUpn, clientKey).catch(
    () => {},
  );
}

/**
 * Server mode: a task change and its timeline row in ONE request. The
 * server only accepts a "reassigned"/"reopened" event (Flow 2c mails it)
 * together with that change, checks it against the task as the change
 * leaves it, sets its recipient itself, and writes it only if the change
 * landed. The event stays best-effort like logEvent: only the change's
 * failure is an error.
 */
async function changeWithEvent(
  taskId: number,
  change: Partial<TaskFields>,
  kind: TaskEventKind,
  body: string,
  actor: TaskActor,
  targetUpn: string,
  /** More timeline rows for the same change (a reopen's new due date). */
  extra: { kind: TaskEventKind; body: string }[] = [],
): Promise<void> {
  const event = (id: string, k: TaskEventKind, text: string, target: string) => ({
    id,
    method: 'POST' as const,
    list: LISTS.taskComments,
    fields: {
      Title: k,
      TaskId: taskId,
      EntryType: 'event',
      EventKind: k,
      Body: text,
      AuthorUpn: actor.upn,
      AuthorName: actor.name,
      TargetUpn: target,
      PostedAt: new Date().toISOString(),
      // One key per call: a request the browser re-sends never makes a second event.
      ClientKey: newClientKey('ev'),
    },
  });
  const res = await executeListOps([
    { id: 'change', method: 'PATCH', list: LISTS.tasks, itemId: String(taskId), fields: change },
    event('event', kind, body, targetUpn),
    ...extra.map((e, i) => event(`event${i + 2}`, e.kind, e.body, '')),
  ]);
  const result = res.get('change');
  if (!result || result.status < 200 || result.status >= 300) {
    const err = (result?.body as { error?: { code?: string; message?: string } } | undefined)?.error;
    throw new GraphError(result?.status ?? 500, err?.code ?? 'taskChangeFailed', err?.message ?? 'The change failed.');
  }
}

/**
 * Complete = done, no verification gate; name captures accountability.
 * `notesAlreadyPosted` = the notes text was just posted as a comment row
 * (task-page flow), so the event row stays plain instead of repeating it.
 */
export async function completeTask(
  taskId: number,
  completedByName: string,
  completionNotes: string,
  actorUpn: string,
  notesAlreadyPosted = false,
  /** The "completed" event's key — the same while the person retries the same completion. */
  clientKey?: string,
): Promise<void> {
  try {
    await updateListItem<TaskFields>(LISTS.tasks, String(taskId), {
      TaskStatus: 'complete',
      CompletedAt: new Date().toISOString(),
      CompletedByName: completedByName,
      CompletionNotes: completionNotes,
    });
  } catch (err) {
    // The change landed but its answer was lost (a retry after a dropped
    // connection): the task IS complete, by THIS person — go on to its event.
    // Completed by someone else first: a real race, say so (QA 2026-10-26).
    if (!(err instanceof GraphError && err.code === 'alreadyComplete')) throw err;
    const now = await getTaskById(taskId);
    if (!now || !equalsIgnoreCase(now.completedByUpn ?? '', actorUpn)) throw err;
  }
  await logEvent(
    taskId,
    'completed',
    completionNotes && !notesAlreadyPosted
      ? `Marked complete — “${completionNotes}”`
      : 'Marked complete',
    { upn: actorUpn, name: completedByName },
    '',
    clientKey,
  );
}

/**
 * Reopen re-queues the task. Notification is EVENT-DRIVEN: Flow 2c reacts
 * to the 'reopened' timeline row (the retired Flow 2b re-fired on every
 * edit of an already-reopened task — due-date changes sent reopen mail).
 * TargetUpn = the assignee as of the event, same race-guard as reassign.
 */
export async function reopenTask(
  task: ServerTask,
  actor: TaskActor,
  reason: string,
  /** New due date (YYYY-MM-DD; owner ruling 2026-10-21 — the old one has usually passed). '' keeps the old date. */
  newDueDate = '',
  /** "Proof required" from here on (B1 — the reopen dialog pre-ticks it); undefined = unchanged. */
  proofRequired?: boolean,
  /** A store undoing its own completion (B3) — undoCompletion. */
  undo = false,
): Promise<void> {
  // The reason rides on the reopen event: the activity feed shows it and
  // Flow 2c's "Task reopened" email quotes it (owner ruling 2026-10-19).
  const why = reason.trim();
  if (!why) throw new Error(undo ? 'Say why you are undoing it.' : 'Say why you are reopening the task.');
  // The server sets the prefix from who asks (a store's reopen is always its undo — S1).
  const body = reopenBody(why, undo);
  const due = newDueDate.trim();
  if (due && !/^\d{4}-\d{2}-\d{2}$/.test(due)) throw new Error('Pick a valid due date.');
  const dateChanges = due !== '' && due !== task.dueDate;
  // LastOverdueReminderAt is left alone (clearing a DateTime via Graph is
  // unproven in-tenant — docs/flows.md Flow 3 Block B).
  const change: Partial<TaskFields> = dateChanges
    ? { TaskStatus: 'reopened', DueDate: due }
    : { TaskStatus: 'reopened' };
  if (proofRequired !== undefined) change.ProofRequired = proofRequired;
  const dueBody = `Due date changed to ${due} (was ${task.dueDate || 'not set'})`;
  if (dataViaApi()) {
    await changeWithEvent(task.id, change, 'reopened', body, actor, task.assignedTo,
      dateChanges ? [{ kind: 'due_date', body: dueBody }] : []);
    return;
  }
  await updateListItem<TaskFields>(LISTS.tasks, String(task.id), change);
  await logEvent(task.id, 'reopened', body, actor, task.assignedTo);
  if (dateChanges) await logEvent(task.id, 'due_date', dueBody, actor);
}

/**
 * A store team undoes its OWN completion (B3, owner rulings 2026-10-25):
 * a reason, the status only — the due date stays (the server refuses
 * anything else, and only inside the window: src/data/taskRules.ts). No
 * email: Flow 2c never mails the reopener, who here is the assignee.
 */
export function undoCompletion(task: ServerTask, actor: TaskActor, reason: string): Promise<void> {
  return reopenTask(task, actor, reason, '', undefined, true);
}

/**
 * Switch "Proof required" on an open task (B1; DMs,
 * Auditors, Admins). No timeline row — the card and task page show it.
 */
export async function setProofRequired(taskId: number, on: boolean): Promise<void> {
  await updateListItem<TaskFields>(LISTS.tasks, String(taskId), { ProofRequired: on });
}

/**
 * Reassign from the task page (Flow 2c emails the new assignee via the
 * 'reassigned' event row — the Tasks-modified trigger only reacts to
 * reopens, docs/flows.md).
 */
export async function reassignTask(
  task: ServerTask,
  newAssignee: string,
  actor: TaskActor,
): Promise<void> {
  if (dataViaApi()) {
    await changeWithEvent(task.id, { AssignedTo: newAssignee }, 'reassigned',
      `Reassigned to ${newAssignee} (was ${task.assignedTo || 'unassigned'})`, actor, newAssignee);
    return;
  }
  await updateListItem<TaskFields>(LISTS.tasks, String(task.id), {
    AssignedTo: newAssignee,
  });
  await logEvent(
    task.id,
    'reassigned',
    `Reassigned to ${newAssignee} (was ${task.assignedTo || 'unassigned'})`,
    actor,
    newAssignee, // Flow 2c's recipient — the assignee AS OF this event
  );
}

/**
 * A task change and the comment that announces it, in ONE request in server
 * mode (the comment only lands if the change did — QA 2026-10-25: a lost
 * comment left the store unable to retry and nobody emailed). Direct
 * (rollback) mode: change, then comment.
 */
async function changeWithComment(
  taskId: number,
  change: Partial<TaskFields>,
  body: string,
  actor: TaskActor,
  notifyUpns: string[],
  clientKey: string,
): Promise<void> {
  if (!dataViaApi()) {
    await updateListItem<TaskFields>(LISTS.tasks, String(taskId), change);
    await addTaskComment(taskId, body, actor.upn, actor.name, notifyUpns, clientKey);
    return;
  }
  const res = await executeListOps([
    { id: 'change', method: 'PATCH', list: LISTS.tasks, itemId: String(taskId), fields: change },
    {
      id: 'comment',
      method: 'POST',
      list: LISTS.taskComments,
      fields: {
        Title: 'comment',
        TaskId: taskId,
        EntryType: 'comment',
        EventKind: '',
        Body: body,
        AuthorUpn: actor.upn,
        AuthorName: actor.name,
        NotifyUpns: notifyUpns.join(';'),
        PostedAt: new Date().toISOString(),
        ClientKey: clientKey,
      },
    },
  ]);
  const failed = (id: string) => {
    const r = res.get(id);
    if (r && r.status >= 200 && r.status < 300) return null;
    const err = (r?.body as { error?: { code?: string; message?: string } } | undefined)?.error;
    return new GraphError(r?.status ?? 500, err?.code ?? 'taskChangeFailed', err?.message ?? 'The change failed.');
  };
  const changeError = failed('change');
  if (changeError) throw changeError;
  const commentError = failed('comment');
  if (commentError) throw commentError;
}

/**
 * "Ask for more time" (B2, owner rulings 2026-10-25): the store's request
 * (the server checks it and stamps who/when) plus a comment, so Flow 2c
 * emails it (the creator by default, plus the store's DM in `notifyUpns`).
 */
export async function askForMoreTime(
  task: ServerTask,
  ask: { date: string; reason: string; name: string },
  actor: TaskActor,
  notifyUpns: string[] = [],
  /** The comment's key — the same while the person retries the same ask. */
  clientKey: string = newClientKey('cmt'),
): Promise<void> {
  const date = ask.date.trim();
  const reason = ask.reason.trim().slice(0, EXTENSION_REASON_MAX);
  const problem = extensionDateProblem(date, task.dueDate, new Date());
  if (problem) throw new Error(problem);
  if (!reason) throw new Error('Say why you need more time.');
  const body = `Asked for more time: until ${formatDay(date)} — ${reason}`;
  try {
    // The full shape (the server rewrites who/when from the token; on the
    // direct rollback path this IS what's stored, so it must parse).
    await changeWithComment(
      task.id,
      { ExtensionRequest: JSON.stringify({ v: 1, by: actor.upn, name: ask.name.trim(), at: new Date().toISOString(), date, reason }) },
      body,
      actor,
      notifyUpns,
      clientKey,
    );
  } catch (err) {
    // A retry after the request landed but its comment didn't: if the
    // pending request is exactly this one, just send the comment (its key
    // makes a repeat harmless).
    if (!(err instanceof GraphError && err.code === 'extensionPending')) throw err;
    const fresh = await getTaskById(task.id);
    const pending = fresh?.extensionRequest;
    if (!pending || pending.date !== date || pending.reason !== reason || !equalsIgnoreCase(pending.by, actor.upn)) throw err;
    await addTaskComment(task.id, body, actor.upn, actor.name, notifyUpns, clientKey);
  }
}

/**
 * Accept the store's request (DM / Auditor / Admin): the due date becomes
 * the asked date and the request is cleared, with a comment that tells the
 * store (Flow 2c emails the assignee) — one request; the server refuses an
 * answer to a request that's no longer pending. Then the usual due-date
 * timeline row (best effort).
 */
export async function acceptMoreTime(task: ServerTask, actor: TaskActor, clientKey: string = newClientKey('cmt')): Promise<void> {
  const request = task.extensionRequest;
  if (!request) throw new Error('There is no request for more time on this task.');
  await changeWithComment(
    task.id,
    { DueDate: request.date, ExtensionRequest: '' },
    `Accepted — new due date ${formatDay(request.date)}.`,
    actor,
    [],
    clientKey,
  );
  await logEvent(task.id, 'due_date', `Due date changed to ${request.date} (was ${task.dueDate || 'not set'})`, actor);
}

/** Decline the store's request with a reason (required); the due date stays. */
export async function declineMoreTime(task: ServerTask, reason: string, actor: TaskActor, clientKey: string = newClientKey('cmt')): Promise<void> {
  const why = reason.trim();
  if (!why) throw new Error('Say why you are declining.');
  await changeWithComment(task.id, { ExtensionRequest: '' }, `Declined the request for more time: ${why}`, actor, [], clientKey);
}

/** Change the due date (Flow 3's overdue math follows the new date). */
export async function updateTaskDueDate(
  task: ServerTask,
  dueDate: string,
  actor: TaskActor,
): Promise<void> {
  await updateListItem<TaskFields>(LISTS.tasks, String(task.id), {
    DueDate: dueDate,
  });
  await logEvent(
    task.id,
    'due_date',
    `Due date changed to ${dueDate} (was ${task.dueDate || 'not set'})`,
    actor,
  );
}

/**
 * Permanently delete a task and EVERYTHING attached to it: the comment/event
 * thread, the uploaded files (the whole Task-{id} folder), and the Tasks row
 * itself — in that order, so a partial failure leaves the task visible and
 * the delete retryable. NotificationLog rows are kept (append-only send
 * history); the task links in that log land on a friendly "no longer
 * exists" page. Conduct roles only — enforced by the calling surface.
 */
export async function deleteTask(taskId: number): Promise<void> {
  // 1. Thread rows, in $batch (≤20 ops per call handled by executeListOps).
  const thread = await getListItems<{ TaskId: number }>(LISTS.taskComments, {
    filter: `fields/TaskId eq ${taskId}`,
  });
  if (thread.length > 0) {
    const requests: ListOp[] = thread.map((row, i) => ({
      id: `d-${i}`,
      method: 'DELETE' as const,
      list: LISTS.taskComments,
      itemId: row.id,
    }));
    const responses = await executeListOps(requests);
    for (const res of responses.values()) {
      // 404 = already gone (a concurrent delete) — that's success here.
      if (res.status >= 400 && res.status !== 404) {
        throw new Error('Couldn’t delete the task’s comments — try again.');
      }
    }
  }

  // 2. Uploaded files — removing the folder removes its files.
  await deleteAttachmentsFolder(`Task-${taskId}`);

  // 3. The task row itself, last.
  await deleteListItem(LISTS.tasks, String(taskId));
}

/**
 * Past its due date in the Eastern calendar (src/ui/dates.ts) — a UTC slice
 * flipped tasks to overdue around 8pm Eastern on the day they were due.
 */
export function isOverdue(task: ServerTask, today = new Date()): boolean {
  if (task.status === 'complete' || !task.dueDate) return false;
  return task.dueDate.slice(0, 10) < companyIsoDay(today);
}
